import crypto from 'crypto';

import { defineMessages, type IntlShape, type MessageDescriptor } from 'react-intl';

import { isSelfAttributedAgentRecord } from './agentClassification.ts';

/** Labels that replace a real identity; `{hash}` is a short id hash, never the id itself. */
export const maskedIdentityI18n: Record<'agent' | 'user', MessageDescriptor> = defineMessages({
	agent: {
		id: 'confluence-analytics.masked-identity.agent',
		defaultMessage: 'Agent {hash}',
		description:
			'The text is shown as an agent identifier label in analytics when the viewing agent is private and its real name is hidden. The placeholder {hash} will be substituted with an anonymized identifier for the agent (e.g. "A2J3KJSO").',
	},
	user: {
		id: 'confluence-analytics.viewers-table.user',
		defaultMessage: 'User {hash}',
		description:
			'The text is shown as a user identifier label in the viewers table on the page analytics view when privacy mode is enabled and the user\'s real name is hidden. The placeholder {hash} will be substituted with an anonymized numeric identifier for the user (e.g. "42").',
	},
});

/** The ids of an agent record. Names, photos and emails are left out so they cannot be read. */
export type MaskableAgentRecord = {
	/** The agent's id; opaque when the API already masked the row. */
	userId?: string | null;
	/** Fallback for the agent's id on responses that do not select `userId`. */
	userProfile?: { id?: string | null } | null;
	/** The related person's id; opaque when the API already masked the row. */
	relatedUserAccountId?: string | null;
	/** Fallback for the related person's id. */
	relatedUser?: { id?: string | null } | null;
};

export type MaskedAgentIdentity = {
	/** Shown in place of the agent's name. */
	agentName: string;
	/** Absent when the record has no person, or the agent is attributed to itself. */
	relatedUserName?: string;
};

// Hashes only an id, so the label stays the same when the surrounding payload changes.
const toShortHash = (id: string | null | undefined): string =>
	crypto
		.createHash('sha256')
		.update(id ?? '')
		.digest('hex')
		.substring(0, 8)
		.toUpperCase();

/** Resolves the labels that replace a private agent's identity and its related person's. */
export const getMaskedAgentIdentity = (
	record: MaskableAgentRecord,
	formatMessage: IntlShape['formatMessage'],
): MaskedAgentIdentity => {
	const agentId = record.userId ?? record.userProfile?.id;
	const relatedId = record.relatedUserAccountId ?? record.relatedUser?.id;
	const agentName = formatMessage(maskedIdentityI18n.agent, { hash: toShortHash(agentId) });
	if (!relatedId || isSelfAttributedAgentRecord({ ...record, accountId: agentId })) {
		return { agentName };
	}
	return {
		agentName,
		relatedUserName: formatMessage(maskedIdentityI18n.user, { hash: toShortHash(relatedId) }),
	};
};
